/**
 * SuperAdmin dashboard - platform-level: institutes, stats, subscription plans, subscriptions, payments
 */

import { useState, useEffect } from 'react';
import api from '../api/axios';
import Layout from '../components/Layout';

const PAYMENT_METHODS = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
];

export default function SuperAdminDashboard() {
  const [institutes, setInstitutes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [accessFilter, setAccessFilter] = useState('all');
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [activeSection, setActiveSection] = useState('institutes');
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    slug: '',
    description: '',
    priceMonthly: 0,
    priceYearly: 0,
    maxStudents: 100,
    maxFaculty: 20,
    features: '',
    isActive: true,
  });
  const [planSubmitting, setPlanSubmitting] = useState(false);

  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [showAssignSubscription, setShowAssignSubscription] = useState(false);
  const [assignSubForm, setAssignSubForm] = useState({
    instituteId: '',
    planId: '',
    billingCycle: 'yearly',
    status: 'active',
  });
  const [assignSubSubmitting, setAssignSubSubmitting] = useState(false);

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsInstituteFilter, setPaymentsInstituteFilter] = useState('');
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    instituteId: '',
    amount: '',
    currency: 'INR',
    paymentMethod: 'upi',
    reference: '',
    notes: '',
  });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  const fetchInstitutes = async () => {
    try {
      const params = new URLSearchParams();
      params.set('limit', '500');
      if (search) params.set('search', search);
      if (activeOnly) params.set('isActive', 'true');
      if (accessFilter && accessFilter !== 'all') params.set('subscriptionAccess', accessFilter);
      const { data } = await api.get(`/super-admin/institutes?${params.toString()}`);
      setInstitutes(data.data?.institutes ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load institutes');
    }
  };

  const openInstituteDetail = async (institute) => {
    setSelectedInstitute(institute);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/super-admin/institutes/${institute.id}`);
      setSelectedInstitute(data.data || institute);
      if (plans.length === 0) fetchPlans();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load institute details');
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/super-admin/stats');
      setStats(data.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load stats');
    }
  };

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const { data } = await api.get('/super-admin/plans');
      setPlans(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    setSubscriptionsLoading(true);
    try {
      const { data } = await api.get('/super-admin/subscriptions');
      setSubscriptions(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load subscriptions');
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const params = new URLSearchParams();
      if (paymentsInstituteFilter) params.set('instituteId', paymentsInstituteFilter);
      const { data } = await api.get(`/super-admin/payments?${params.toString()}`);
      setPayments(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchInstitutes(), fetchStats(), fetchPlans()]).finally(() => setLoading(false));
  }, [search, activeOnly, accessFilter]);

  useEffect(() => {
    if (activeSection === 'plans') fetchPlans();
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'subscriptions') {
      fetchSubscriptions();
      if (plans.length === 0) fetchPlans();
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'payments') fetchPayments();
  }, [activeSection, paymentsInstituteFilter]);

  const toggleInstituteActive = async (institute) => {
    try {
      await api.patch(`/super-admin/institutes/${institute.id}`, {
        is_active: !institute.is_active,
      });
      await fetchInstitutes();
      await fetchStats();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to update institute');
    }
  };

  const openEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name || '',
      slug: plan.slug || '',
      description: plan.description || '',
      priceMonthly: plan.price_monthly ?? 0,
      priceYearly: plan.price_yearly ?? 0,
      maxStudents: plan.max_students ?? 100,
      maxFaculty: plan.max_faculty ?? 20,
      features: Array.isArray(plan.features) ? plan.features.join('\n') : '',
      isActive: plan.is_active !== false,
    });
    setShowPlanForm(true);
  };

  const submitPlan = async (e) => {
    e.preventDefault();
    setPlanSubmitting(true);
    setError('');
    try {
      const payload = {
        name: planForm.name,
        slug: planForm.slug || undefined,
        description: planForm.description || undefined,
        priceMonthly: Number(planForm.priceMonthly) || 0,
        priceYearly: Number(planForm.priceYearly) || 0,
        maxStudents: Number(planForm.maxStudents) || 100,
        maxFaculty: Number(planForm.maxFaculty) || 20,
        features: planForm.features ? planForm.features.split('\n').map((s) => s.trim()).filter(Boolean) : [],
        isActive: planForm.isActive,
      };
      if (editingPlanId) {
        await api.patch(`/super-admin/plans/${editingPlanId}`, payload);
        setShowPlanForm(false);
        setEditingPlanId(null);
        fetchPlans();
      } else {
        await api.post('/super-admin/plans', payload);
        setShowPlanForm(false);
        setPlanForm({ name: '', slug: '', description: '', priceMonthly: 0, priceYearly: 0, maxStudents: 100, maxFaculty: 20, features: '', isActive: true });
        fetchPlans();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to save plan');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const submitAssignSubscription = async (e) => {
    e.preventDefault();
    if (!assignSubForm.instituteId || !assignSubForm.planId) {
      setError('Select institute and plan');
      return;
    }
    setAssignSubSubmitting(true);
    setError('');
    try {
      await api.post(`/super-admin/institutes/${assignSubForm.instituteId}/subscription`, {
        planId: assignSubForm.planId,
        billingCycle: assignSubForm.billingCycle,
        status: assignSubForm.status,
      });
      setShowAssignSubscription(false);
      setAssignSubForm({ instituteId: '', planId: '', billingCycle: 'yearly', status: 'active' });
      fetchSubscriptions();
      fetchInstitutes();
      fetchStats();
      if (selectedInstitute?.id === assignSubForm.instituteId) {
        openInstituteDetail({ id: assignSubForm.instituteId });
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to assign subscription');
    } finally {
      setAssignSubSubmitting(false);
    }
  };

  const submitRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.instituteId || !paymentForm.amount || Number(paymentForm.amount) <= 0) {
      setError('Select institute and enter a valid amount');
      return;
    }
    setPaymentSubmitting(true);
    setError('');
    try {
      await api.post('/super-admin/payments', {
        instituteId: paymentForm.instituteId,
        amount: Number(paymentForm.amount),
        currency: paymentForm.currency || 'INR',
        paymentMethod: paymentForm.paymentMethod,
        reference: paymentForm.reference || undefined,
        notes: paymentForm.notes || undefined,
      });
      setShowRecordPayment(false);
      setPaymentForm({ instituteId: '', amount: '', currency: 'INR', paymentMethod: 'upi', reference: '', notes: '' });
      fetchPayments();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to record payment');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const grantAccess = async (instituteId, planId, billingCycle = 'yearly', status = 'active') => {
    if (!instituteId || !planId) {
      setError('Select institute and plan');
      return;
    }
    setAssignSubSubmitting(true);
    setError('');
    try {
      await api.post(`/super-admin/institutes/${instituteId}/subscription`, {
        planId,
        billingCycle,
        status,
      });
      await Promise.all([fetchInstitutes(), fetchSubscriptions(), fetchStats()]);
      if (selectedInstitute?.id === instituteId) {
        await openInstituteDetail({ id: instituteId });
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to grant subscription access');
    } finally {
      setAssignSubSubmitting(false);
    }
  };

  const revokeAccess = async (instituteId) => {
    setAssignSubSubmitting(true);
    setError('');
    try {
      await api.patch(`/super-admin/institutes/${instituteId}/subscription`, { status: 'cancelled' });
      await Promise.all([fetchInstitutes(), fetchSubscriptions(), fetchStats()]);
      if (selectedInstitute?.id === instituteId) {
        await openInstituteDetail({ id: instituteId });
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to revoke subscription access');
    } finally {
      setAssignSubSubmitting(false);
    }
  };

  const restoreAccess = async (instituteId) => {
    setAssignSubSubmitting(true);
    setError('');
    try {
      await api.patch(`/super-admin/institutes/${instituteId}/subscription`, { status: 'active' });
      await Promise.all([fetchInstitutes(), fetchSubscriptions(), fetchStats()]);
      if (selectedInstitute?.id === instituteId) {
        await openInstituteDetail({ id: instituteId });
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to restore subscription access');
    } finally {
      setAssignSubSubmitting(false);
    }
  };

  const accessBadge = (access) => {
    const key = access || 'none';
    const labels = {
      granted: 'Access granted',
      none: 'No subscription',
      expired: 'Expired',
      cancelled: 'Cancelled',
      past_due: 'Past due',
    };
    return (
      <span className={`stu-chip sa-access-${key}`}>
        {labels[key] || key}
      </span>
    );
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  };

  const locationText = (inst) =>
    [inst.city, inst.state, inst.country].filter(Boolean).join(', ') || '—';

  const programsText = (programs) => {
    if (Array.isArray(programs)) return programs.filter(Boolean).join(', ') || '—';
    return programs || '—';
  };

  const sectionTabs = [
    { id: 'institutes', label: 'Institutes' },
    { id: 'plans', label: 'Subscription Plans' },
    { id: 'subscriptions', label: 'Institute Subscriptions' },
    { id: 'payments', label: 'Payments' },
  ];

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="page-title">Super Admin</h1>
          <p className="page-subtitle">Manage the entire application, institutes, and subscription billing.</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="card p-4">
              <p className="text-slate-500 text-sm">Institutes</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.total_institutes}</p>
              <p className="text-xs text-slate-400">{stats.active_institutes} active</p>
            </div>
            <div className="card p-4">
              <p className="text-slate-500 text-sm">Users</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.total_users}</p>
            </div>
            <div className="card p-4">
              <p className="text-slate-500 text-sm">Students</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.total_students}</p>
            </div>
            <div className="card p-4">
              <p className="text-slate-500 text-sm">Faculty</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.total_faculty}</p>
            </div>
            <div className="card p-4">
              <p className="text-slate-500 text-sm">Subscription access</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.subscriptions_granted ?? 0}</p>
              <p className="text-xs text-slate-400">{stats.subscriptions_none ?? 0} without a plan</p>
            </div>
            <div className="card p-4">
              <p className="text-slate-500 text-sm">Feedback submissions</p>
              <p className="text-2xl font-semibold text-slate-800">{stats.total_feedback_submissions}</p>
            </div>
          </div>
        )}

        <nav className="flex flex-wrap gap-1 border-b-2 border-slate-200 pb-0" aria-label="Sections">
          {sectionTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveSection(tab.id)}
              className={`nav-tab ${activeSection === tab.id ? 'nav-tab-active' : 'nav-tab-inactive'}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {error && (
          <div className="alert alert-warning" role="alert">
            {error}
          </div>
        )}

        {activeSection === 'institutes' && (
          <div className="space-y-6">
            <div className="card overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-4">
                <h2 className="text-lg font-semibold text-slate-800">All institutes</h2>
                <input
                  type="text"
                  placeholder="Search name, code, city, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input max-w-xs"
                />
                <select value={accessFilter} onChange={(e) => setAccessFilter(e.target.value)} className="input max-w-[200px]">
                  <option value="all">All subscription access</option>
                  <option value="granted">Access granted</option>
                  <option value="none">No subscription</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="past_due">Past due</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                  Active only
                </label>
              </div>
              {loading ? (
                <div className="p-8 text-center text-slate-500">Loading…</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table min-w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Institute</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Contact</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Usage</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Plan</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Access</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {institutes.map((inst) => (
                        <tr key={inst.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <p className="text-slate-800 font-medium m-0">{inst.name}</p>
                            <p className="text-xs text-slate-500 m-0">
                              {inst.institute_code || inst.slug}
                              {inst.title ? ` · ${inst.title}` : ''}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">{locationText(inst)}</td>
                          <td className="px-4 py-3 text-sm">
                            <p className="m-0 text-slate-800">{inst.contact_name || '—'}</p>
                            <p className="m-0 text-slate-500 text-xs">{inst.email}</p>
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {inst.students_count ?? 0} students · {inst.faculty_count ?? 0} faculty
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {inst.plan_name || '—'}
                            {inst.expires_at ? <span className="block text-xs text-slate-400">Expires {formatDate(inst.expires_at)}</span> : null}
                          </td>
                          <td className="px-4 py-3">{accessBadge(inst.subscription_access)}</td>
                          <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                            <button type="button" onClick={() => openInstituteDetail(inst)} className="text-sm text-primary-600 hover:underline">
                              View details
                            </button>
                            <button type="button" onClick={() => toggleInstituteActive(inst)} className="text-sm text-primary-600 hover:underline">
                              {inst.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {institutes.length === 0 && <div className="p-8 text-center text-slate-500">No institutes found.</div>}
                </div>
              )}
            </div>

            {selectedInstitute && (
              <div className="card p-5 space-y-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800 m-0">{selectedInstitute.name}</h2>
                    <p className="text-sm text-slate-500 m-0">{selectedInstitute.title || selectedInstitute.slug}</p>
                  </div>
                  <button type="button" onClick={() => setSelectedInstitute(null)} className="px-3 py-1.5 border border-slate-300 rounded-lg text-slate-700 text-sm">
                    Close
                  </button>
                </div>
                {detailLoading ? (
                  <p className="text-slate-500 text-sm">Loading details…</p>
                ) : (
                  <>
                    <dl className="sa-detail-grid">
                      <div><dt>Institute code</dt><dd>{selectedInstitute.institute_code || '—'}</dd></div>
                      <div><dt>Slug</dt><dd>{selectedInstitute.slug}</dd></div>
                      <div><dt>Status</dt><dd>{selectedInstitute.is_active ? 'Active' : 'Inactive'}</dd></div>
                      <div><dt>Contact</dt><dd>{selectedInstitute.contact_name || '—'}</dd></div>
                      <div><dt>Email</dt><dd>{selectedInstitute.email}</dd></div>
                      <div><dt>Parent society</dt><dd>{selectedInstitute.parent_society || '—'}</dd></div>
                      <div><dt>Established</dt><dd>{formatDate(selectedInstitute.established_on)}</dd></div>
                      <div><dt>Location</dt><dd>{locationText(selectedInstitute)}</dd></div>
                      <div><dt>Address</dt><dd>{selectedInstitute.address || '—'}</dd></div>
                      <div><dt>Pincode</dt><dd>{selectedInstitute.pincode || '—'}</dd></div>
                      <div><dt>NAAC</dt><dd>{selectedInstitute.naac_accredited ? `${selectedInstitute.naac_grade || 'Accredited'} (${selectedInstitute.naac_accreditation_year || '—'})` : 'No'}</dd></div>
                      <div><dt>NBA</dt><dd>{selectedInstitute.nba_accredited ? `Accredited (${selectedInstitute.nba_accreditation_year || '—'})` : 'No'}</dd></div>
                      <div><dt>Users</dt><dd>{selectedInstitute.stats?.users ?? selectedInstitute.users_count ?? 0}</dd></div>
                      <div><dt>Students</dt><dd>{selectedInstitute.stats?.students ?? selectedInstitute.students_count ?? 0}</dd></div>
                      <div><dt>Faculty</dt><dd>{selectedInstitute.stats?.faculty ?? selectedInstitute.faculty_count ?? 0}</dd></div>
                      <div><dt>Feedback cycles</dt><dd>{selectedInstitute.stats?.cycles ?? selectedInstitute.cycles_count ?? 0}</dd></div>
                      <div><dt>Programs</dt><dd>{programsText(selectedInstitute.programs_offered)}</dd></div>
                    </dl>
                    {selectedInstitute.short_profile && (
                      <p className="text-sm text-slate-600 m-0">{selectedInstitute.short_profile}</p>
                    )}

                    <div className="border-t border-slate-200 pt-4 space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-base font-semibold text-slate-800 m-0">Subscription access</h3>
                        {accessBadge(selectedInstitute.subscription_access)}
                      </div>
                      {selectedInstitute.subscription_id ? (
                        <dl className="sa-detail-grid">
                          <div><dt>Plan</dt><dd>{selectedInstitute.plan_name || '—'}</dd></div>
                          <div><dt>Status</dt><dd>{selectedInstitute.subscription_status || '—'}</dd></div>
                          <div><dt>Billing</dt><dd>{selectedInstitute.billing_cycle || '—'}</dd></div>
                          <div><dt>Started</dt><dd>{formatDate(selectedInstitute.started_at)}</dd></div>
                          <div><dt>Expires</dt><dd>{formatDate(selectedInstitute.expires_at)}</dd></div>
                          <div><dt>Limits</dt><dd>{selectedInstitute.max_students ?? '—'} students, {selectedInstitute.max_faculty ?? '—'} faculty</dd></div>
                          <div><dt>Price</dt><dd>₹{selectedInstitute.price_monthly ?? 0} / ₹{selectedInstitute.price_yearly ?? 0}</dd></div>
                        </dl>
                      ) : (
                        <p className="text-sm text-slate-500 m-0">This institute has no subscription. Grant a plan below to give platform access.</p>
                      )}
                      {Array.isArray(selectedInstitute.features) && selectedInstitute.features.length > 0 && (
                        <ul className="list-disc list-inside text-sm text-slate-700 m-0">
                          {selectedInstitute.features.map((feature, idx) => (
                            <li key={idx}>{typeof feature === 'string' ? feature : JSON.stringify(feature)}</li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="input-label">Grant / change plan</label>
                          <select
                            className="input min-w-[200px]"
                            defaultValue=""
                            onChange={(e) => {
                              const planId = e.target.value;
                              if (planId) grantAccess(selectedInstitute.id, planId);
                              e.target.value = '';
                            }}
                            disabled={assignSubSubmitting}
                          >
                            <option value="">Select plan</option>
                            {plans.filter((p) => p.is_active !== false).map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                        {selectedInstitute.subscription_id && selectedInstitute.subscription_access !== 'granted' && (
                          <button type="button" disabled={assignSubSubmitting} onClick={() => restoreAccess(selectedInstitute.id)} className="btn-primary">
                            Restore access
                          </button>
                        )}
                        {selectedInstitute.subscription_access === 'granted' && (
                          <button
                            type="button"
                            disabled={assignSubSubmitting}
                            onClick={() => revokeAccess(selectedInstitute.id)}
                            className="px-3 py-1.5 border border-slate-300 rounded-lg text-slate-700 text-sm"
                          >
                            Revoke access
                          </button>
                        )}
                      </div>
                    </div>

                    {Array.isArray(selectedInstitute.payments) && selectedInstitute.payments.length > 0 && (
                      <div className="border-t border-slate-200 pt-4">
                        <h3 className="text-base font-semibold text-slate-800 mb-2">Recent payments</h3>
                        <div className="overflow-x-auto">
                          <table className="table min-w-full">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase">Method</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase">Reference</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedInstitute.payments.map((pay) => (
                                <tr key={pay.id}>
                                  <td className="px-3 py-2 text-sm">{pay.currency} {Number(pay.amount).toLocaleString()}</td>
                                  <td className="px-3 py-2 text-sm">{pay.payment_method}</td>
                                  <td className="px-3 py-2 text-sm">{pay.reference || '—'}</td>
                                  <td className="px-3 py-2 text-sm">{formatDate(pay.paid_at || pay.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {activeSection === 'plans' && (
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-slate-800">Subscription Plans</h2>
              <button type="button" onClick={() => { setEditingPlanId(null); setPlanForm({ name: '', slug: '', description: '', priceMonthly: 0, priceYearly: 0, maxStudents: 100, maxFaculty: 20, features: '', isActive: true }); setShowPlanForm(true); }} className="btn-primary">
                Add Plan
              </button>
            </div>
            {showPlanForm && (
              <form onSubmit={submitPlan} className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
                <h3 className="text-sm font-semibold text-slate-700">{editingPlanId ? 'Edit plan' : 'Create plan'}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="input-label">Name *</label>
                    <input type="text" value={planForm.name} onChange={(e) => setPlanForm((f) => ({ ...f, name: e.target.value }))} required className="input" placeholder="e.g. Pro" />
                  </div>
                  <div>
                    <label className="input-label">Slug</label>
                    <input type="text" value={planForm.slug} onChange={(e) => setPlanForm((f) => ({ ...f, slug: e.target.value }))} className="input" placeholder="e.g. pro" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="input-label">Description</label>
                    <input type="text" value={planForm.description} onChange={(e) => setPlanForm((f) => ({ ...f, description: e.target.value }))} className="input" placeholder="Short description" />
                  </div>
                  <div>
                    <label className="input-label">Price (monthly)</label>
                    <input type="number" min="0" step="0.01" value={planForm.priceMonthly} onChange={(e) => setPlanForm((f) => ({ ...f, priceMonthly: e.target.value }))} className="input" />
                  </div>
                  <div>
                    <label className="input-label">Price (yearly)</label>
                    <input type="number" min="0" step="0.01" value={planForm.priceYearly} onChange={(e) => setPlanForm((f) => ({ ...f, priceYearly: e.target.value }))} className="input" />
                  </div>
                  <div>
                    <label className="input-label">Max students</label>
                    <input type="number" min="1" value={planForm.maxStudents} onChange={(e) => setPlanForm((f) => ({ ...f, maxStudents: e.target.value }))} className="input" />
                  </div>
                  <div>
                    <label className="input-label">Max faculty</label>
                    <input type="number" min="1" value={planForm.maxFaculty} onChange={(e) => setPlanForm((f) => ({ ...f, maxFaculty: e.target.value }))} className="input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="input-label">Features (one per line)</label>
                    <textarea value={planForm.features} onChange={(e) => setPlanForm((f) => ({ ...f, features: e.target.value }))} className="input" rows={3} placeholder="Feature 1&#10;Feature 2" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="planActive" checked={planForm.isActive} onChange={(e) => setPlanForm((f) => ({ ...f, isActive: e.target.checked }))} />
                    <label htmlFor="planActive" className="text-sm text-slate-600">Active</label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={planSubmitting} className="btn-primary">{planSubmitting ? 'Saving…' : 'Save'}</button>
                  <button type="button" onClick={() => { setShowPlanForm(false); setEditingPlanId(null); }} className="px-3 py-1.5 border border-slate-300 rounded-lg text-slate-700 text-sm">Cancel</button>
                </div>
              </form>
            )}
            {plansLoading ? (
              <div className="p-8 text-center text-slate-500">Loading plans…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table min-w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Monthly / Yearly</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Limits</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {plans.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-800 font-medium">{p.name}</td>
                        <td className="px-4 py-3 text-slate-600">₹{p.price_monthly ?? 0} / ₹{p.price_yearly ?? 0}</td>
                        <td className="px-4 py-3 text-slate-600">{p.max_students} students, {p.max_faculty} faculty</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${p.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                            {p.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={() => openEditPlan(p)} className="text-sm text-primary-600 hover:underline">Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {plans.length === 0 && !plansLoading && <div className="p-8 text-center text-slate-500">No plans. Create one above.</div>}
              </div>
            )}
          </div>
        )}

        {activeSection === 'subscriptions' && (
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-slate-800">Institute Subscriptions</h2>
              <button type="button" onClick={() => { setAssignSubForm({ instituteId: '', planId: '', billingCycle: 'yearly', status: 'active' }); setShowAssignSubscription(true); }} className="btn-primary">
                Assign subscription
              </button>
            </div>
            {showAssignSubscription && (
              <form onSubmit={submitAssignSubscription} className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
                <h3 className="text-sm font-semibold text-slate-700">Assign plan to institute</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="input-label">Institute *</label>
                    <select value={assignSubForm.instituteId} onChange={(e) => setAssignSubForm((f) => ({ ...f, instituteId: e.target.value }))} required className="input">
                      <option value="">Select institute</option>
                      {institutes.map((i) => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="input-label">Plan *</label>
                    <select value={assignSubForm.planId} onChange={(e) => setAssignSubForm((f) => ({ ...f, planId: e.target.value }))} required className="input">
                      <option value="">Select plan</option>
                      {plans.filter((p) => p.is_active !== false).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="input-label">Billing cycle</label>
                    <select value={assignSubForm.billingCycle} onChange={(e) => setAssignSubForm((f) => ({ ...f, billingCycle: e.target.value }))} className="input">
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="input-label">Status</label>
                    <select value={assignSubForm.status} onChange={(e) => setAssignSubForm((f) => ({ ...f, status: e.target.value }))} className="input">
                      <option value="trial">Trial</option>
                      <option value="active">Active</option>
                      <option value="past_due">Past due</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={assignSubSubmitting} className="btn-primary">{assignSubSubmitting ? 'Assigning…' : 'Assign'}</button>
                  <button type="button" onClick={() => setShowAssignSubscription(false)} className="px-3 py-1.5 border border-slate-300 rounded-lg text-slate-700 text-sm">Cancel</button>
                </div>
              </form>
            )}
            {subscriptionsLoading ? (
              <div className="p-8 text-center text-slate-500">Loading subscriptions…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table min-w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Institute</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Billing</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Access</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {subscriptions.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-800 font-medium">{s.institute_name}</td>
                        <td className="px-4 py-3 text-slate-600">{s.plan_name}</td>
                        <td className="px-4 py-3 text-slate-600">{s.billing_cycle || '—'}</td>
                        <td className="px-4 py-3">{accessBadge(s.expires_at && new Date(s.expires_at) < new Date() ? 'expired' : (s.status === 'active' || s.status === 'trial' ? 'granted' : s.status))}</td>
                        <td className="px-4 py-3 text-slate-600">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {subscriptions.length === 0 && !subscriptionsLoading && <div className="p-8 text-center text-slate-500">No subscriptions. Assign a plan to an institute above.</div>}
              </div>
            )}
          </div>
        )}

        {activeSection === 'payments' && (
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-slate-800">Payments</h2>
              <div className="flex flex-wrap items-center gap-3">
                <select value={paymentsInstituteFilter} onChange={(e) => setPaymentsInstituteFilter(e.target.value)} className="input max-w-[200px]">
                  <option value="">All institutes</option>
                  {institutes.map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => { setPaymentForm({ instituteId: paymentsInstituteFilter || '', amount: '', currency: 'INR', paymentMethod: 'upi', reference: '', notes: '' }); setShowRecordPayment(true); }} className="btn-primary">
                  Record payment
                </button>
              </div>
            </div>
            {showRecordPayment && (
              <form onSubmit={submitRecordPayment} className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
                <h3 className="text-sm font-semibold text-slate-700">Record payment</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="input-label">Institute *</label>
                    <select value={paymentForm.instituteId} onChange={(e) => setPaymentForm((f) => ({ ...f, instituteId: e.target.value }))} required className="input">
                      <option value="">Select institute</option>
                      {institutes.map((i) => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="input-label">Amount *</label>
                    <input type="number" min="0.01" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm((f) => ({ ...f, amount: e.target.value }))} required className="input" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="input-label">Currency</label>
                    <input type="text" value={paymentForm.currency} onChange={(e) => setPaymentForm((f) => ({ ...f, currency: e.target.value }))} className="input" maxLength={3} />
                  </div>
                  <div>
                    <label className="input-label">Payment method *</label>
                    <select value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm((f) => ({ ...f, paymentMethod: e.target.value }))} className="input">
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="input-label">Reference</label>
                    <input type="text" value={paymentForm.reference} onChange={(e) => setPaymentForm((f) => ({ ...f, reference: e.target.value }))} className="input" placeholder="Transaction ID / cheque no" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="input-label">Notes</label>
                    <textarea value={paymentForm.notes} onChange={(e) => setPaymentForm((f) => ({ ...f, notes: e.target.value }))} className="input" rows={2} placeholder="Optional notes" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={paymentSubmitting} className="btn-primary">{paymentSubmitting ? 'Recording…' : 'Record payment'}</button>
                  <button type="button" onClick={() => setShowRecordPayment(false)} className="px-3 py-1.5 border border-slate-300 rounded-lg text-slate-700 text-sm">Cancel</button>
                </div>
              </form>
            )}
            {paymentsLoading ? (
              <div className="p-8 text-center text-slate-500">Loading payments…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table min-w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Institute</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Method</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {payments.map((pay) => (
                      <tr key={pay.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-800 font-medium">{pay.institute_name}</td>
                        <td className="px-4 py-3 text-slate-600">{pay.currency} {Number(pay.amount).toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-600">{PAYMENT_METHODS.find((m) => m.value === pay.payment_method)?.label || pay.payment_method}</td>
                        <td className="px-4 py-3 text-slate-600">{pay.reference || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{pay.paid_at ? new Date(pay.paid_at).toLocaleString() : (pay.created_at ? new Date(pay.created_at).toLocaleString() : '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {payments.length === 0 && !paymentsLoading && <div className="p-8 text-center text-slate-500">No payments. Record one above.</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
